import {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import { GridSortChangeEvent } from "@progress/kendo-react-grid";
import {
  keepPreviousData,
  QueryKey,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import compact from "lodash-es/compact";
import isEmpty from "lodash-es/isEmpty";
import noop from "lodash-es/noop";
import values from "lodash-es/values";
import React, {
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { PageChangeEvent } from "@/components/Kendo/Grid";
import getMock from "@/configs/mock";
import { DEFAULT_PER_PAGE } from "@/configs/site";
import { getMockData } from "@/helpers/getMockData";
import { useIsInMockScope } from "@/libs/mockScope";

/** What `useQuery`/`usePaginateQuery` pass a method as its second argument. */
export type RequestOptions = { signal?: AbortSignal };

export type QueryMethod<Args, Response> = (
  args: Args,
  options?: RequestOptions,
) => Promise<Response>;

export type Sort = {
  field: string;
  dir: "DESC" | "ASC";
};

type PaginatedResponse = {
  total?: number;
  items?: unknown;
  pageNo?: number;
};

type PaginateQueryOptions<Response> = Omit<
  UseQueryOptions<Response, unknown, Response, QueryKey>,
  "queryKey" | "queryFn"
>;

interface Props<Args extends Record<string, unknown>, Response> {
  queryKey?: QueryKey;
  method: QueryMethod<Args, Response>;
  params?: Args;
  /**
   * The initial server sort. Omit it to leave the order to the server: no
   * `sortBy` / `sortOrder` is sent and the grid shows no sort indicator.
   */
  sort?: SortDescriptor;
  clientSortFields?: string[];
  options?: PaginateQueryOptions<Response>;
  perPage?: number;
  page?: number;
  onPageChange?: (args: PageChangeEvent) => void;
  onFilterChange?: (value: CompositeFilterDescriptor) => void;
}

const DEFAULT_PAGINATE_QUERY_STALE_TIME = 30 * 1000;

function usePaginateQuery<
  Args extends Record<string, unknown>,
  Response extends PaginatedResponse,
>(args: Props<Args, Response>) {
  const {
    method,
    params = {} as Args,
    sort,
    options = {},
    page = 1,
    perPage = DEFAULT_PER_PAGE,
    onPageChange: _onPageChange = noop,
    onFilterChange: _onFilterChange = noop,
    clientSortFields = [],
  } = args;
  const { ...queryOptions } = options;
  const firstRender = useRef(true);
  const paramsLen = useMemo(() => compact(values(params)), [params]);
  const paramsKey = useMemo(() => JSON.stringify(paramsLen), [paramsLen]);
  const [pageSize, setPageSize] = useState<number>(perPage);
  const [pageNo, setPageNo] = useState<number>(page);
  const [sortOrder, setOrderBy] = useState<Sort["dir"] | undefined>(
    sort?.dir?.toUpperCase() as Sort["dir"] | undefined,
  );
  const [sortBy, setSortBy] = React.useState<string | undefined>(sort?.field);
  const queryKey = [method.name, ...values(params)];

  const requestParams = useMemo(
    () =>
      ({
        ...params,
        pageNo,
        pageSize,
        sortOrder,
        sortBy,
      }) as Args,
    [params, pageNo, pageSize, sortOrder, sortBy],
  );

  const isInMockScope = useIsInMockScope();
  const isMock = getMock(method, requestParams, isInMockScope);
  const useMock = !isEmpty(isMock);

  const onPageChange = useCallback(
    (data: PageChangeEvent) => {
      const { page, perPage } = data;
      setPageNo(page);
      setPageSize(perPage);
      _onPageChange(data);
    },
    [_onPageChange],
  );

  const onFilterChange = useCallback(
    (value: CompositeFilterDescriptor) => {
      _onFilterChange(value);
    },
    [_onFilterChange],
  );

  const onSortChange = useCallback(
    (data: GridSortChangeEvent) => {
      const next =
        (!clientSortFields.includes(data.sort[0]?.field) && data.sort[0]) ||
        sort;

      // Cleared with no initial sort to fall back to: back to server order.
      setOrderBy(
        next ? (next.dir?.toUpperCase() as Sort["dir"]) || "ASC" : undefined,
      );
      setSortBy(next?.field);
    },
    [clientSortFields, sort],
  );

  const { data, isLoading, isFetching, refetch, error } = useQuery<
    Response,
    unknown,
    Response,
    QueryKey
  >({
    queryKey: args.queryKey ?? [
      ...queryKey,
      pageNo,
      pageSize,
      sortOrder,
      sortBy,
    ],
    queryFn: () =>
      useMock
        ? Promise.resolve(getMockData<Args, Response>(method, requestParams))
        : method(requestParams),
    placeholderData: keepPreviousData,
    staleTime: DEFAULT_PAGINATE_QUERY_STALE_TIME,
    ...queryOptions,
  });

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
    } else {
      setPageNo(1);
    }
  }, [paramsKey]);

  useEffect(() => {
    setPageNo(page);
    setPageSize(perPage);
  }, [page, perPage]);

  return {
    data,
    isLoading,
    refetch,
    pagination: {
      sort: sort ? [sort] : [],
      serverPagination: true,
      clientSortFields,
      total: data?.total ?? 0,
      page: pageNo,
      perPage: pageSize,
      onPageChange,
      onSortChange,
      onFilterChange,
      isLoading: isLoading || isFetching,
    },
    isFetching,
    error,
  };
}

export type PaginateQuery = {
  sort: SortDescriptor[];
  serverPagination: boolean;
  total: number;
  page: number;
  perPage: number;
  onPageChange: (value: PageChangeEvent) => void;
  onSortChange: (value: GridSortChangeEvent) => void;
  onFilterChange: (value: CompositeFilterDescriptor) => void;
  isLoading: boolean;
};

export default usePaginateQuery;
